import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { UncontrolledCollapsibleListSection } from 'ui-components/components/collapsible-list/collapsible-list-section/uncontrolled';
import { INLINE_MESSAGE_TYPES } from 'ui-components/components/inline-message';

import CustomsWidgetContainer from '../../../components/customs-declarations/container';
import { useEnableLocalDeliveryDriverTip } from '../../../feature-flags/hooks/use-enable-local-delivery-driver-tip';
import ConfirmationOptionContainer from '../../../fulfillment-planner/confirmation-option/container';
import { useFulfillmentPlannerContext } from '../../../fulfillment-planner/context';
import fulfillmentPlannerMessages from '../../../fulfillment-planner/messages';
import FulfillmentPlannerContextDeliveryTimeContainer from '../../../fulfillment-planner/delivery-time/container';
import DutiesAndTaxes from '../../../fulfillment-planner/duties-and-taxes';
import Error from '../../../fulfillment-planner/error/index';
import InsuranceOptionContainer from '../../../fulfillment-planner/insurance-option/container';
import LabeledRow from '../../../fulfillment-planner/labeled-row';
import messages from '../../../fulfillment-planner/labeled-row/messages';
import DriverTip from '../../../fulfillment-planner/local-delivery/driver-tip/csw-driven-input';
import PickupInformationByFulfillmentPlanId from '../../../fulfillment-planner/local-delivery/pickup-information/by-fulfillment-plan-id';
import PickupDropdown from '../../../fulfillment-planner/local-delivery/pickup/dropdown';
import FulfillmentPlannerContextDrivenOtherShippingOptions from '../../../fulfillment-planner/other-shipping-options';
import FulfillmentPlannerContextPickupTimeContainer from '../../../fulfillment-planner/pickup-time/container';
import PresetsContainer from '../../../fulfillment-planner/presets/container';
import RequestedShippingService from '../../../fulfillment-planner/requested-shipping-service';
import { RestrictedShipmentSection } from '../../../fulfillment-planner/restricted-shipment-config';
import ShipDate from '../../../fulfillment-planner/ship-date/index';
import ShipFromComponent from '../../../fulfillment-planner/ship-from';
import ShipFromValidator from '../../../fulfillment-planner/ship-from/validator';
import ShippingService from '../../../fulfillment-planner/shipping-service';
import FedexHalShipmentTooltip from '../../../fulfillment-planner/shipping-service/fedex-hal-shipment-tooltip';
import { useFulfillmentRestrictionsByFPId } from '../../../hooks/entities/selectors/fulfillment-plans';
import { useIsFedexHALByFulfillmentPlanId } from '../../../hooks/orders/use-fedex-hal-check';
import { useIsLocalDeliveryServiceSelected } from '../../../fulfillment-planner/hooks/use-is-local-delivery-service-selected';
import { FulfillmentPlanService } from '../../../services/fulfillment-plan-service';
import { InsuredAmount } from '../insured-amount';
import { PackagePlanner } from '../package-planner';
import { PrintButtonContainer } from '../print-button/container';
import { RateContainer } from '../rate';

import style from './style.module.less';

/**
 * @param {object} props
 * @param {string} props.fulfillmentPlanId
 * @param {JSX.Element} props.RestrictedBanner
 * @param {JSX.Element?} props.AdditionalContent
 * @param {boolean?} props.isTikTokShipping
 * @param {boolean?} props.showEnhancedConfig
 * @returns {JSX.Element}
 */
const RestrictedConfiguration = ({
  AdditionalContent = null,
  fulfillmentPlanId,
  isTikTokShipping = false,
  RestrictedBanner,
  showEnhancedConfig = false,
}) => {
  if (!showEnhancedConfig) {
    return RestrictedBanner ? <RestrictedBanner /> : null;
  }

  return (
    <>
      {RestrictedBanner && <RestrictedBanner />}
      <PackagePlanner fulfillmentPlanId={fulfillmentPlanId} />
      <div className={style['fields-and-preset']}>
        <div className={style.fields}>
          {isTikTokShipping ? (
            <LabeledRow label={<FormattedMessage {...messages.shipFrom} />} labelClassName={style.label}>
              <FormattedMessage {...fulfillmentPlannerMessages.tikTokShipFromText} />
            </LabeledRow>
          ) : (
            <ShipFromValidator fulfillmentPlanId={fulfillmentPlanId}>
              {({ validation }) => (
                <LabeledRow
                  label={<FormattedMessage {...messages.shipFrom} />}
                  labelClassName={classNames(
                    style.label,
                    {
                      [style['lable-with-helper-text__drop-down']]: validation.helperText,
                    },
                    {
                      [style.error]: validation.helperTextType === INLINE_MESSAGE_TYPES.error,
                    },
                  )}
                >
                  <ShipFromComponent validation={validation} />
                </LabeledRow>
              )}
            </ShipFromValidator>
          )}
          {AdditionalContent && (
            <div className={style['additional-restricted-content-wrapper']}>
              <AdditionalContent className={style['additional-restricted-content']} />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export const FulfillmentPlanner = ({ fulfillmentPlanId }) => {
  const isFedexHalShipment = useIsFedexHALByFulfillmentPlanId(fulfillmentPlanId);
  const isLocalDelivery = useIsLocalDeliveryServiceSelected();
  const uiRestrictions = useFulfillmentRestrictionsByFPId(fulfillmentPlanId);
  const isDriverTipEnabled = useEnableLocalDeliveryDriverTip();
  const hidePlanner = FulfillmentPlanService.hasLabelConfigurationRestriction(uiRestrictions);
  const { availableDutiesOptions } = useFulfillmentPlannerContext();
  const shouldShowDDP = !!availableDutiesOptions?.length;

  return (
    <div className={style.planner}>
      <div className={style['field-section-scroll']}>
        <div className={style['field-section']}>
          {hidePlanner ? (
            <RestrictedShipmentSection Config={RestrictedConfiguration} fulfillmentPlanId={fulfillmentPlanId} />
          ) : (
            <>
              <PackagePlanner fulfillmentPlanId={fulfillmentPlanId} />
              <div className={style['fields-and-preset']}>
                <div className={style.preset}>
                  <PresetsContainer fulfillmentPlanId={fulfillmentPlanId} />
                </div>
                <div className={style.fields}>
                  <LabeledRow label={<FormattedMessage {...messages.requested} />} labelClassName={style.label}>
                    <RequestedShippingService fulfillmentPlanId={fulfillmentPlanId} />
                    {/* TODO: Browse Rates */}
                  </LabeledRow>
                  <ShipFromValidator fulfillmentPlanId={fulfillmentPlanId}>
                    {({ validation }) => (
                      <LabeledRow
                        label={<FormattedMessage {...messages.shipFrom} />}
                        labelClassName={classNames(
                          style.label,
                          {
                            [style['lable-with-helper-text__drop-down']]: validation.helperText,
                          },
                          {
                            [style.error]: validation.helperTextType === INLINE_MESSAGE_TYPES.error,
                          },
                        )}
                      >
                        <ShipFromComponent validation={validation} />
                      </LabeledRow>
                    )}
                  </ShipFromValidator>
                  <LabeledRow label='Service' labelClassName={style.label} tooltip={isFedexHalShipment && <FedexHalShipmentTooltip />}>
                    <ShippingService fulfillmentPlanId={fulfillmentPlanId} />
                  </LabeledRow>
                  {isLocalDelivery && (
                    <LabeledRow label={<FormattedMessage {...messages.localPickupTime} />} labelClassName={style.label}>
                      <PickupDropdown />
                    </LabeledRow>
                  )}
                  <LabeledRow label={<FormattedMessage {...messages.confirmation} />} labelClassName={style.label}>
                    <ConfirmationOptionContainer fulfillmentPlanId={fulfillmentPlanId} />
                  </LabeledRow>
                  {!isLocalDelivery && (
                    <LabeledRow label={<FormattedMessage {...messages.insurance} />} labelClassName={style.label}>
                      <InsuranceOptionContainer fulfillmentPlanId={fulfillmentPlanId} />
                    </LabeledRow>
                  )}
                  {!isLocalDelivery && <InsuredAmount fulfillmentPlanId={fulfillmentPlanId} />}
                  {shouldShowDDP && (
                    <LabeledRow label={<FormattedMessage {...messages.dutiesAndTaxes} />} labelClassName={style.label}>
                      <DutiesAndTaxes fulfillmentPlanId={fulfillmentPlanId} showBanner />
                    </LabeledRow>
                  )}
                  {isLocalDelivery && isDriverTipEnabled && (
                    <LabeledRow label={<FormattedMessage {...messages.driverTip} />} labelClassName={style.label}>
                      <DriverTip fulfillmentPlanId={fulfillmentPlanId} />
                    </LabeledRow>
                  )}
                  <Error className={style['error-box-container']} />
                  <RateContainer fulfillmentPlanId={fulfillmentPlanId} />
                  {!isLocalDelivery && (
                    <LabeledRow label={<FormattedMessage {...messages.shipDate} />} labelClassName={style.label}>
                      <ShipDate />
                    </LabeledRow>
                  )}
                  {isLocalDelivery && (
                    <LabeledRow label={<FormattedMessage {...messages.pickupTime} />} labelClassName={style.label}>
                      <FulfillmentPlannerContextPickupTimeContainer />
                    </LabeledRow>
                  )}
                  <LabeledRow label={<FormattedMessage {...messages.deliveryTime} />} labelClassName={style.label}>
                    <FulfillmentPlannerContextDeliveryTimeContainer />
                  </LabeledRow>
                </div>
              </div>
            </>
          )}
        </div>
        <FulfillmentPlannerContextDrivenOtherShippingOptions />
        <div className={style['customs-declarations']}>
          <CustomsWidgetContainer fulfillmentPlanId={fulfillmentPlanId} readOnly={false} />
        </div>
        {isLocalDelivery && (
          <UncontrolledCollapsibleListSection header={<FormattedMessage {...messages.pickupInfoSectionHeader} />} theme='white'>
            <PickupInformationByFulfillmentPlanId fulfillmentPlanId={fulfillmentPlanId} />
          </UncontrolledCollapsibleListSection>
        )}
      </div>
      <PrintButtonContainer fulfillmentPlanId={fulfillmentPlanId} />
    </div>
  );
};

FulfillmentPlanner.propTypes = {
  fulfillmentPlanId: PropTypes.string.isRequired,
};
